<script setup lang="ts">
    import { computed, onMounted, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'

    import { useCriteriaPageQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'

    import type {
        CriteriaPageReadModel,
        CriterionAnalysisReadModel
    } from '@Quest/PortfolioScenarios/readmodels'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))


    // ======================
    // Query
    // ======================

    const {
        runCriteriaPageQueryV1,
        modelCriteriaPageQueryV1
    } = useCriteriaPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelCriteriaPageQueryV1 as Ref<CriteriaPageReadModel | null>


    // ======================
    // Local state
    // ======================

    const criteriaFilter = ref<string>('Objectives')


    // ======================
    // Computed (was class getters)
    // ======================

    const allCriteria = computed<CriterionAnalysisReadModel[]>(() =>
        rm.value?.Criteria ?? []
    )

    const goal = computed<CriterionAnalysisReadModel | null>(() =>
        allCriteria.value.find(c => c.IsGoal) ?? null
    )

    const criteria = computed<CriterionAnalysisReadModel[]>(() =>
    {
        const filter = criteriaFilter.value

        if (filter === 'Objectives')
        {
            return allCriteria.value.filter(a => a.IsTop)
        }
        else if (filter === 'Attributes')
        {
            return allCriteria.value.filter(a => a.IsBottom)
        }
        else if (filter === 'All criteria')
        {
            return allCriteria.value.filter(a => !a.IsGoal)
        }

        return []
    })


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Plan - Criteria"
        runCriteriaPageQueryV1()
    })
</script>

<template>
    <div>

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" v-sanitizedHref="`/#/portfolio-plan/projects/${workspaceId}/${tchProjectId}/${scenarioId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back to plan
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{rm.ScenarioName}}</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">

                </div>
            </div>

            <hr class="mb-5" />

            <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">
                <form class="form-inline my-2 my-lg-0">
                    <div class="dropdown">
                        <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-toggle="dropdown">
                            {{criteriaFilter}}
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="criteriaFilter = 'Objectives'">Objectives (top)</a>
                            <a class="dropdown-item" @click="criteriaFilter = 'Attributes'">Attributes (bottom)</a>
                            <a class="dropdown-item" @click="criteriaFilter = 'All criteria'">All criteria</a>
                        </div>
                    </div>
                </form>
            </nav>

            <div>
                <h4 class="mt-5">1. Criteria weights</h4>
                <div class="table-responsive mt-3">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Criteria</th>
                                <th>Local Weight [%]</th>
                                <th>Global Weight [%]</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="c in criteria" :key="c.Id">
                                <td>
                                    <span v-if="!c.IsTop"><small class="text-muted">...{{c.Path}}</small><br /></span>
                                    {{c.Name}}
                                </td>
                                <td>{{c.LocalWeight}}</td>
                                <td>{{c.GlobalWeight}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4 class="mt-5">2. Criteria weights vs. Portfolio value share</h4>
                <div class="table-responsive mt-3">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Criteria</th>
                                <th>Weight [%]</th>
                                <th>Value Share [%]</th>
                                <th>Diff [%]</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="c in criteria" :key="c.Id">
                                <td>
                                    <span v-if="!c.IsTop"><small class="text-muted">...{{c.Path}}</small><br /></span>
                                    {{c.Name}}
                                </td>
                                <td>{{c.GlobalWeight}}</td>
                                <td>{{c.GlobalValueShare}}</td>
                                <td>{{c.Diff}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4 class="mt-5">3. Project Value Stats</h4>
                <div class="table-responsive mt-3">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th rowspan="2">Criteria</th>
                                <th colspan="4">Project Value Stats</th>
                            </tr>
                            <tr>
                                <th>Avg value</th>
                                <th>Min value</th>
                                <th>Max value</th>
                                <th>Std dev</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="table-secondary" v-if="goal">
                                <td>{{goal.Name}}</td>
                                <td>{{goal.AvgValue}}</td>
                                <td>{{goal.MinValue}}</td>
                                <td>{{goal.MaxValue}}</td>
                                <td>{{goal.StdDev}}</td>
                            </tr>
                            <tr v-for="c in criteria" :key="c.Id">
                                <td>
                                    <span v-if="!c.IsTop"><small class="text-muted">...{{c.Path}}</small><br /></span>
                                    {{c.Name}}
                                </td>
                                <td>{{c.AvgValue}}</td>
                                <td>{{c.MinValue}}</td>
                                <td>{{c.MaxValue}}</td>
                                <td>{{c.StdDev}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4 class="mt-5">4. No. of Projects by Value</h4>
                <div class="table-responsive mt-3">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th rowspan="2">Criteria</th>
                                <th colspan="5">No. of Projects by Value</th>
                            </tr>
                            <tr>
                                <th>0</th>
                                <th>0-25</th>
                                <th>25-50</th>
                                <th>50-75</th>
                                <th>75-100</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="table-secondary" v-if="goal">
                                <td>{{goal.Name}}</td>
                                <td>{{goal.ScoresCount0}}</td>
                                <td>{{goal.ScoresCount1}}</td>
                                <td>{{goal.ScoresCount2}}</td>
                                <td>{{goal.ScoresCount3}}</td>
                                <td>{{goal.ScoresCount4}}</td>
                            </tr>
                            <tr v-for="c in criteria" :key="c.Id">
                                <td>
                                    <span v-if="!c.IsTop"><small class="text-muted">...{{c.Path}}</small><br /></span>
                                    {{c.Name}}
                                </td>
                                <td>{{c.ScoresCount0}}</td>
                                <td>{{c.ScoresCount1}}</td>
                                <td>{{c.ScoresCount2}}</td>
                                <td>{{c.ScoresCount3}}</td>
                                <td>{{c.ScoresCount4}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
